import { LocationType } from "src/components/utility/LocationUtils";
import { UitkEGMapsQualifier } from "@egds/react-core/map";
import { LatLng } from "@eg-maps/core";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";

type TrackFunction = (
  rfrr: string,
  link: string,
  delay?: boolean | undefined,
  additionalUisPrimeMicroMessages?: any
) => void;

export const iconMap: any = {
  [LocationType.AIRPORT]: {
    type: "place",
    qualifiers: [UitkEGMapsQualifier.AIRPORT],
    status: "unknown",
  },
  soldOut: {
    type: "property",
    qualifiers: [UitkEGMapsQualifier.NOT_CLUSTERED],
    status: "sold_out",
  },
  [LocationType.POINT_OF_INTEREST]: {
    type: "place",
    qualifiers: [],
    status: "unknown",
  },
  price: {
    type: "property",
    status: "available",
  },
  [LocationType.TRAIN_STATION]: {
    type: "place",
    qualifiers: [UitkEGMapsQualifier.TRANSIT],
    status: "unknown",
  },
  [LocationType.METRO_STATION]: {
    type: "place",
    qualifiers: [UitkEGMapsQualifier.TRANSIT],
    status: "unknown",
  },
};

export const convertToEgMapsMarkers = (
  mapMarkers: any[],
  formatText: (key: string, ...args: any[]) => string
): any[] => {
  return mapMarkers.map((marker) => {
    const newMarker = {
      id: marker?.id,
      lat: marker?.latitude,
      lng: marker?.longitude,
      text: marker?.formattedPrice || 0,
      ...(iconMap[marker?.icon?.type] ?? {
        type: "place",
        status: "unknown",
      }),
    };
    if (newMarker.type === "property") {
      newMarker.name = marker?.placeName;
      newMarker.price = marker?.formattedPrice;
    } else if (newMarker.qualifiers?.includes(UitkEGMapsQualifier.AIRPORT) && marker?.flight) {
      newMarker.name = marker.flight.destination?.largeName;
      newMarker.price = marker.flight.formattedTicketPrice;
    } else {
      newMarker.name = marker?.placeName;
    }
    newMarker.ariaLabels = {
      default: formatText("egmaps.markers.unselected.a11yLabel", newMarker.name) || `${newMarker.name} marker`,
      onSelect: formatText("egmaps.markers.selected.a11yLabel", newMarker.name) || `${newMarker.name} marker selected`,
    };
    return newMarker;
  });
};

export type ViewportChangeCallback = (center: any, bounds: any, zoom: number) => void;

export const registerViewportChangeListener = (
  map: any,
  onZoomIn?: ViewportChangeCallback,
  onZoomOut?: ViewportChangeCallback,
  onMapPan?: ViewportChangeCallback
) => {
  if (onZoomIn || onZoomOut || onMapPan) {
    map.addBoundChangeListener((event: any) => {
      switch (event.originalEvent) {
        case "zoomIn":
        case "zoomInKeyboard":
          onZoomIn?.(event.bounds.center, event.bounds, event.zoom);
          break;
        case "zoomOut":
        case "zoomOutKeyboard":
          onZoomOut?.(event.bounds.center, event.bounds, event.zoom);
          break;
        default:
          onMapPan?.(event.bounds.center, event.bounds, event.zoom);
          break;
      }
    });
  }
};

export const mapTracking = (componentName: string, track: TrackFunction, rfrrString?: string) => {
  const flexAnalyticsInfo: FlexTrackingInfo = {
    moduleName: componentName,
    action: Action.CLICK,
    rfrr: rfrrString,
  };

  sendDelayedClickstreamTrackEvent(flexAnalyticsInfo, track);
};

export const getCenter = (map: any) => {
  const [lat, lng] = map.getBounds().center;
  const center: LatLng = {
    lat,
    lng,
  };
  return center;
};
